import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, Loader2, ExternalLink, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "cerebrum_session_v2"; // bump = wipe any v1 sessions on first load

// Client-side staleness limits
const MAX_SESSION_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours since first created → start fresh
const MAX_IDLE_MS = 60 * 60 * 1000;            // 1 hour without activity → start fresh

function loadStoredSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.session_id || !data.session_token) return null;
    const now = Date.now();
    if (data.created_at && now - data.created_at > MAX_SESSION_AGE_MS) return null;
    if (data.last_active && now - data.last_active > MAX_IDLE_MS) return null;
    return data;
  } catch (_) {
    return null;
  }
}

function saveStoredSession(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (_) {}
}

function clearStoredSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // also nuke legacy v1 key if present
    localStorage.removeItem("cerebrum_session_id_v1");
  } catch (_) {}
}

function CerebrumOrb({ onClick, hasUnread }) {
  return (
    <motion.button
      data-testid="cerebrum-orb"
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.6, type: "spring" }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.96 }}
      className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[60] group"
      aria-label="Open Cerebrum chat"
    >
      <span className="absolute inset-0 rounded-full bg-cerebrum-violet/60 blur-2xl opacity-70 group-hover:opacity-100 transition-opacity" />
      <span className="relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-cerebrum-violet to-[#5B2EFF] border border-white/15 shadow-[0_8px_40px_-10px_rgba(139,92,246,0.7)]">
        <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={1.8} />
        {hasUnread && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-rogue-emerald border border-geminii-deep" />
        )}
      </span>
      <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-geminii-deep/95 border border-white/10 text-[11px] font-mono text-white/80 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <Sparkles className="w-3 h-3 text-cerebrum-violet" />
        Ask Cerebrum
      </span>
    </motion.button>
  );
}

function Bubble({ role, text, streaming }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`cerebrum-msg-${role}`}
        className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-cerebrum-violet/90 text-white rounded-br-sm"
            : "bg-white/[0.04] border border-white/[0.06] text-geminii-muted rounded-bl-sm"
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{text}</p>
        ) : (
          <div className="prose prose-invert prose-sm max-w-none prose-p:my-1.5 prose-p:text-geminii-muted prose-strong:text-white prose-a:text-cerebrum-violet prose-a:no-underline hover:prose-a:underline prose-code:text-rogue-cyan prose-code:bg-black/30 prose-code:px-1 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-pre:bg-black/40 prose-pre:border prose-pre:border-white/[0.06] prose-ul:my-1.5 prose-li:my-0.5 prose-li:text-geminii-muted">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{text || (streaming ? "​" : "")}</ReactMarkdown>
            {streaming && (
              <span className="inline-block w-1.5 h-3.5 ml-0.5 align-text-bottom bg-cerebrum-violet animate-pulse" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadCapturedToast({ email }) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[88%] flex items-center gap-2 rounded-2xl px-4 py-2 text-xs bg-rogue-emerald/15 border border-rogue-emerald/30 text-rogue-emerald">
        <Check className="w-3.5 h-3.5" />
        Saved — Dirk will reach out to <span className="font-mono">{email}</span>.
      </div>
    </div>
  );
}

export default function CerebrumWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionTokenRef = useRef(null);
  const [messages, setMessages] = useState([]); // {role, text, lead?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Touch last_active in localStorage on every interaction so we can detect "idle for an hour".
  const touchActive = useCallback(() => {
    const cur = loadStoredSession();
    if (cur) saveStoredSession({ ...cur, last_active: Date.now() });
  }, []);

  // Session bootstrap (lazy — only when widget first opens)
  const ensureSession = useCallback(async () => {
    // 1. Try a still-fresh stored session
    const stored = loadStoredSession();
    if (stored) {
      sessionTokenRef.current = stored.session_token;
      setSessionId(stored.session_id);
      try {
        const r = await fetch(`${API}/api/cerebrum/session/${stored.session_id}`, {
          headers: { "X-Session-Token": stored.session_token },
        });
        if (r.ok) {
          const data = await r.json();
          if (data.messages?.length) {
            setMessages(data.messages.map((m) => ({ role: m.role, text: m.text })));
            saveStoredSession({ ...stored, last_active: Date.now() });
            return stored.session_id;
          }
        } else if (r.status === 403 || r.status === 404) {
          // Token mismatch or gone — wipe and start clean
          clearStoredSession();
        }
      } catch (_) {}
    } else {
      // Wipe any stale session before creating a new one
      clearStoredSession();
    }

    // 2. Fresh session
    try {
      const r = await fetch(`${API}/api/cerebrum/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page_path: location.pathname,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });
      const data = await r.json();
      const now = Date.now();
      sessionTokenRef.current = data.session_token;
      saveStoredSession({
        session_id: data.session_id,
        session_token: data.session_token,
        created_at: now,
        last_active: now,
      });
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", text: data.greeting }]);
      return data.session_id;
    } catch (e) {
      setMessages([{ role: "assistant", text: "I couldn't connect right now. Please try again in a moment." }]);
      return null;
    }
  }, [location.pathname]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setHasUnread(false);
    if (!sessionId) await ensureSession();
    setTimeout(() => inputRef.current?.focus(), 240);
  }, [sessionId, ensureSession]);

  const handleClose = useCallback(() => setOpen(false), []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    const sid = sessionId || (await ensureSession());
    if (!sid) return;
    touchActive();
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "", streaming: true }]);
    setSending(true);

    try {
      const resp = await fetch(`${API}/api/cerebrum/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          message: text,
          page_path: location.pathname,
        }),
      });
      if (!resp.ok || !resp.body) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let capturedLead = null;
      // Read SSE chunks
      // Process line-by-line, each "data: ..." event terminated by \n\n
      // Streaming-safe partial-line handling
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // keep last partial
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, text: (last.text || "") + data.text };
                }
                return copy;
              });
            } else if (data.type === "lead") {
              capturedLead = data.email;
            } else if (data.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  text: `_Something tripped: ${data.message}. Try again?_`,
                  streaming: false,
                };
                return copy;
              });
            } else if (data.type === "done") {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, streaming: false };
                }
                if (capturedLead) copy.push({ role: "lead", text: capturedLead });
                return copy;
              });
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        // Replace the streaming placeholder with the error
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && last.streaming) {
          copy[copy.length - 1] = {
            role: "assistant",
            text: `_Something tripped: ${e.message}. Try again?_`,
            streaming: false,
          };
        } else {
          copy.push({ role: "assistant", text: `_Something tripped: ${e.message}. Try again?_` });
        }
        return copy;
      });
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, ensureSession, location.pathname, touchActive]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Suggested prompts (rotate based on current page)
  const suggestions = (() => {
    const p = location.pathname;
    if (p.startsWith("/space/kinly")) return [
      "How is Kinly different from Cozi?",
      "When does Kinly launch?",
      "Tell me about the founders pricing",
    ];
    if (p.startsWith("/nexus")) return [
      "What sensors does Nexus support?",
      "How does it compare to Ignition or Node-RED?",
      "I run a 200-ha farm — would Nexus work for me?",
    ];
    if (p.startsWith("/space/rogue")) return [
      "Walk me through a typical Rogue workflow.",
      "How much for a 12-user company?",
      "Can Rogue replace Salesforce + Asana?",
    ];
    if (p.startsWith("/cerebrum")) return [
      "Show off — write me a haiku about operators.",
      "Can I embed Cerebrum in my own app?",
      "What models power you?",
    ];
    return [
      "What is Geminii in one paragraph?",
      "Which product should I look at first?",
      "Schedule a call with the founder.",
    ];
  })();

  return (
    <>
      <CerebrumOrb onClick={handleOpen} hasUnread={hasUnread} />

      <AnimatePresence>
        {open && (
          <>
            {/* Mobile-only backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 z-[59] bg-black/50 md:hidden"
              data-testid="cerebrum-backdrop"
            />

            <motion.div
              data-testid="cerebrum-panel"
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.96 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="fixed z-[60] bg-geminii-deep border border-white/10 shadow-[0_20px_80px_-10px_rgba(139,92,246,0.4)] flex flex-col
                         inset-x-3 bottom-3 top-16
                         md:inset-auto md:right-7 md:bottom-7 md:top-auto md:w-[420px] md:h-[640px]
                         rounded-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.06] bg-gradient-to-r from-cerebrum-violet/15 to-transparent">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-cerebrum-violet/60 blur-md" />
                    <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-cerebrum-violet to-[#5B2EFF] flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-display font-semibold text-white text-sm">Cerebrum</span>
                      <span className="text-[9px] font-mono uppercase tracking-wider text-cerebrum-violet bg-cerebrum-violet/10 border border-cerebrum-violet/30 px-1.5 py-0.5 rounded">live</span>
                    </div>
                    <span className="text-[10px] font-mono text-geminii-dim">claude sonnet 4.5 · page-aware</span>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  data-testid="cerebrum-close"
                  className="p-1.5 rounded-lg text-geminii-dim hover:text-white hover:bg-white/[0.06] transition"
                  aria-label="Close Cerebrum"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Messages */}
              <div
                ref={scrollRef}
                data-testid="cerebrum-messages"
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              >
                {messages.length === 0 && (
                  <div className="text-center text-xs text-geminii-dim py-8">Loading…</div>
                )}
                {messages.map((m, i) => {
                  if (m.role === "lead") return <LeadCapturedToast key={i} email={m.text} />;
                  // Suppress empty streaming bubble until first token arrives (we show standalone thinking indicator below)
                  if (m.role === "assistant" && !m.text && m.streaming) return null;
                  return <Bubble key={i} role={m.role} text={m.text} streaming={m.streaming} />;
                })}
                {sending && messages[messages.length - 1]?.role === "assistant" && !messages[messages.length - 1]?.text && (
                  <div className="flex justify-start">
                    <div className="bg-white/[0.04] border border-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2 text-geminii-dim text-xs">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-cerebrum-violet" />
                      Cerebrum is thinking…
                    </div>
                  </div>
                )}

                {/* Suggestions — only show when no user messages yet */}
                {!sending && messages.length <= 1 && (
                  <div className="pt-2 space-y-1.5">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-geminii-dim px-1">Try asking</div>
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        data-testid="cerebrum-suggestion"
                        onClick={() => {
                          setInput(s);
                          setTimeout(() => inputRef.current?.focus(), 30);
                        }}
                        className="w-full text-left text-xs px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-geminii-muted hover:bg-white/[0.05] hover:border-cerebrum-violet/30 hover:text-white transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="border-t border-white/[0.06] p-3">
                <div className="relative flex items-end gap-2 bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-2 focus-within:border-cerebrum-violet/50 transition">
                  <textarea
                    ref={inputRef}
                    data-testid="cerebrum-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={1}
                    placeholder="Ask Cerebrum anything…"
                    className="flex-1 bg-transparent text-sm text-white placeholder:text-geminii-dim outline-none resize-none max-h-32 py-1"
                    style={{ minHeight: "24px" }}
                  />
                  <button
                    onClick={send}
                    disabled={!input.trim() || sending}
                    data-testid="cerebrum-send"
                    className="p-1.5 rounded-lg bg-cerebrum-violet text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-cerebrum-violet/90 transition flex-shrink-0"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Powered-by footer (also the "add to your site" upsell) */}
                <Link
                  to="/cerebrum"
                  onClick={handleClose}
                  data-testid="cerebrum-embed-cta"
                  className="mt-2.5 flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-geminii-dim hover:text-cerebrum-violet transition group"
                >
                  <Sparkles className="w-3 h-3" />
                  Powered by Cerebrum
                  <span className="text-geminii-dim/60">·</span>
                  <span className="group-hover:underline">Add it to your site</span>
                  <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
